import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    type MailSignature,
    mailSignatureService,
    invalidateSignatureCache,
} from "../../services/mailSignatureService";
import { useMailContext } from "../../context/MailContext";
import { canSendMailbox } from "../../utils/mailboxAccess";
import { SandboxedMailBody } from "./SandboxedMailBody";
import { RichTextEditor, RichTextEditorHandle } from "./richtext/RichTextEditor";
import {
    sanitizeComposeHtml,
    SIGNATURE_IMAGE_MAX_BYTES,
    SIGNATURE_EMBEDDED_TOTAL_MAX_BYTES,
    SIGNATURE_HTML_MAX_CHARS,
} from "./composeSanitize";
import {
    describeDefaultToggle,
    describeSignatureScope,
    embeddedImageBytes,
    isOrphanMailbox,
    mailboxLabel,
} from "./signatureSectionLogic";
import { ConfirmDialog } from "../redesign/ConfirmDialog";
import { Toast } from "../redesign/Toast";
import { MiniSpinner } from "../chat/chatUi";
import { I } from "../redesign/kvpIcons";
import "./mailSettings.css";
import { reportMailError } from "../../services/mailError";
import { meldeSignaturAenderung } from "./mailSignaturBus";

/**
 * Obergrenze für das gespeicherte HTML — spiegelt `@MaxLength` im Backend.
 *
 * Liegt bei den übrigen Größengrenzen in `composeSanitize.ts`, weil sie an der
 * Bildgrenze hängt: Bilder stecken als `data:`-URI im HTML. Stünde die Zahl
 * weiter hier in der Komponente, ließe sich die Bildgrenze anheben, ohne dass
 * jemand diese mitzieht — und das Speichern scheiterte dann mit einer Meldung,
 * die den Zusammenhang nicht verrät.
 */
const HTML_MAX_CHARS = SIGNATURE_HTML_MAX_CHARS;

const kb = (bytes: number) => Math.round(bytes / 1024);

/**
 * CRUD-Section für Mail-Signaturen.
 *
 * Signaturen werden im WYSIWYG-Editor gepflegt (vorher: HTML-Quelltext in einer
 * Monospace-Textarea) und können ein eingebettetes Logo enthalten. Der Standard
 * gilt je Postfach: „Allgemein" greift, wenn für das gewählte Absender-Postfach
 * kein eigener Standard gesetzt ist.
 */
export const MailSignatureSection: React.FC = () => {
    const { mailboxes } = useMailContext();
    const sendable = useMemo(() => mailboxes.filter(canSendMailbox), [mailboxes]);

    const [rows, setRows] = useState<MailSignature[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [savingId, setSavingId] = useState<number | null>(null);

    // editingId === null → Neuanlage.
    const [editingId, setEditingId] = useState<number | null>(null);
    const [name, setName] = useState("");
    const [initialHtml, setInitialHtml] = useState("");
    const [isDefault, setIsDefault] = useState(false);
    const [mailboxAddress, setMailboxAddress] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const editorRef = useRef<RichTextEditorHandle>(null);
    const [preview, setPreview] = useState("");

    const [toast, setToast] = useState<{ msg: string; tone: "error" | "success" } | null>(null);
    const [confirmRow, setConfirmRow] = useState<MailSignature | null>(null);

    useEffect(() => {
        void loadAll();
    }, []);

    const loadAll = async () => {
        setLoading(true);
        setError(null);
        try {
            // Cache umgehen: nach einem Schreibvorgang muss die Liste frisch sein.
            invalidateSignatureCache();
            setRows(await mailSignatureService.list());
        } catch (error) {
            setError(reportMailError({
                area: "mail",
                action: "load-settings",
                error,
                fallbackMessage: "Signaturen konnten nicht geladen werden.",
            }));
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setEditingId(null);
        setName("");
        setInitialHtml("");
        setPreview("");
        setIsDefault(false);
        setMailboxAddress("");
    };

    const startEdit = (row: MailSignature) => {
        setEditingId(row.id);
        setName(row.name);
        setInitialHtml(row.html);
        setPreview(row.html);
        setIsDefault(row.isDefault);
        setMailboxAddress(row.mailboxAddress ?? "");
    };

    const refreshPreview = () => setPreview(editorRef.current?.getHtml() ?? "");

    const handleSubmit = async () => {
        if (!name.trim()) return;

        const html = sanitizeComposeHtml(editorRef.current?.getHtml() ?? "");

        // Vorab prüfen, damit statt eines Server-400 eine verständliche Meldung
        // erscheint. Der Server prüft autoritativ erneut.
        const imgBytes = embeddedImageBytes(html);
        if (imgBytes > SIGNATURE_EMBEDDED_TOTAL_MAX_BYTES) {
            setToast({
                msg: `Die eingebetteten Bilder sind zusammen zu groß (${kb(imgBytes)} KB, erlaubt sind ${kb(SIGNATURE_EMBEDDED_TOTAL_MAX_BYTES)} KB). Bitte kleinere Bilder verwenden.`,
                tone: "error",
            });
            return;
        }
        if (html.length > HTML_MAX_CHARS) {
            setToast({ msg: "Die Signatur ist insgesamt zu groß. Bitte Bilder verkleinern.", tone: "error" });
            return;
        }

        setSubmitting(true);
        try {
            // `scope` wird bewusst NICHT mitgesendet — siehe MailTemplateSection.
            // Weglassen erhält den gespeicherten Wert; ein hartes "user" würde
            // eine büroweite Signatur beim Speichern still auf privat herabstufen.
            const payload = { name: name.trim(), html, isDefault, mailboxAddress };
            if (editingId === null) await mailSignatureService.create(payload);
            else await mailSignatureService.update(editingId, payload);

            // Komplett neu laden: Das Setzen eines Standards verdrängt einen
            // anderen — nur ein Reload zeigt das korrekt. `loadAll` leert dabei
            // auch den Cache, damit offene Verfassen-Fenster nicht bis zu einer
            // Minute mit der alten Liste arbeiten.
            await loadAll();
            resetForm();
            // Offene Verfassen-Fenster ziehen nach — wer gerade über „Signatur
            // erstellen…" hierher kam, findet sie sofort in seiner Mail.
            meldeSignaturAenderung();
            setToast({ msg: "Signatur gespeichert.", tone: "success" });
        } catch (error) {
            setToast({
                msg: reportMailError({
                    area: "mail",
                    action: "save-settings",
                    error,
                    fallbackMessage: "Die Signatur konnte nicht gespeichert werden.",
                }),
                tone: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemove = async (row: MailSignature) => {
        setSavingId(row.id);
        try {
            await mailSignatureService.remove(row.id);
            setRows((prev) => prev.filter((r) => r.id !== row.id));
            if (editingId === row.id) resetForm();
            meldeSignaturAenderung();
            setToast({ msg: "Signatur gelöscht.", tone: "success" });
        } catch (error) {
            setToast({
                msg: reportMailError({
                    area: "mail",
                    action: "delete-settings",
                    error,
                    fallbackMessage: "Die Signatur konnte nicht gelöscht werden.",
                }),
                tone: "error",
            });
        } finally {
            setSavingId(null);
            setConfirmRow(null);
        }
    };

    if (loading) {
        return (
            <div className="mset">
                <div className="mset-card" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <MiniSpinner />
                    <span style={{ fontSize: 14 }}>Signaturen werden geladen …</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="mset">
                <div className="mset-card" style={{ color: "var(--mset-err)" }}>{error}</div>
            </div>
        );
    }

    // Zeigt die bearbeitete Signatur auf ein nicht mehr zugewiesenes Postfach,
    // wird dieses zusätzlich angeboten — sonst ginge die Zuordnung beim Speichern
    // anderer Felder still verloren.
    const editedOrphan =
        mailboxAddress && !sendable.some((m) => m.mailboxAddress.toLowerCase() === mailboxAddress.toLowerCase())
            ? mailboxAddress
            : null;

    return (
        <div className="mset">
            <div className="mset-card">
                <h2 className="mset-title">E-Mail-Signaturen</h2>
                <div className="mset-hint" style={{ marginTop: -8, marginBottom: 16 }}>
                    Die Signatur des gewählten Absender-Postfachs wird beim Verfassen automatisch
                    eingesetzt — sonst die allgemeine.
                </div>

                <div className="mset-grid2">
                    <div>
                        <label className="mset-label">Name</label>
                        <input
                            className="mset-inp"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="z. B. Standard-Signatur"
                        />
                    </div>
                    <div>
                        <label className="mset-label">Standard für Postfach</label>
                        <select
                            className="mset-sel"
                            value={mailboxAddress}
                            onChange={(e) => setMailboxAddress(e.target.value)}
                        >
                            <option value="">Allgemein (alle Postfächer)</option>
                            {sendable.map((m) => (
                                <option key={m.id} value={m.mailboxAddress.toLowerCase()}>
                                    {mailboxLabel(m)}
                                </option>
                            ))}
                            {editedOrphan && (
                                <option value={editedOrphan}>{editedOrphan} (nicht mehr zugewiesen)</option>
                            )}
                        </select>
                    </div>
                </div>

                <div className="mset-block">
                    <label className="mset-cbx">
                        <input
                            type="checkbox"
                            checked={isDefault}
                            onChange={(e) => setIsDefault(e.target.checked)}
                        />
                        <span className="mset-cbx-box">{isDefault ? I.check : null}</span>
                        {describeDefaultToggle(mailboxAddress)}
                    </label>
                </div>

                <div className="mset-block">
                    <label className="mset-label">Inhalt</label>
                    <div className="mset-editor">
                        {/* `key` erzwingt Remount beim Wechsel der bearbeiteten Zeile —
                            sonst würde der Inhalt der zuvor bearbeiteten Signatur
                            gespeichert. */}
                        <RichTextEditor
                            key={editingId ?? "new"}
                            ref={editorRef}
                            iconVariant="kvp"
                            initialHtml={initialHtml}
                            placeholder="Mit freundlichen Grüßen …"
                            ariaLabel="Signatur-Inhalt"
                            imageMaxBytes={SIGNATURE_IMAGE_MAX_BYTES}
                            onError={(m) => setToast({ msg: m, tone: "error" })}
                        />
                    </div>
                    <div className="mset-hint">
                        Logo per Bild-Schaltfläche, Zwischenablage oder Drag&amp;Drop einfügen —
                        max. {kb(SIGNATURE_IMAGE_MAX_BYTES)} KB pro Bild,
                        {" "}{kb(SIGNATURE_EMBEDDED_TOTAL_MAX_BYTES)} KB insgesamt. Beim Senden wird das
                        Bild automatisch als Anhang mitgeschickt, damit Outlook es anzeigt.
                    </div>
                </div>

                <div className="mset-actions">
                    <button
                        type="button"
                        className="mset-btn primary"
                        disabled={submitting || !name.trim()}
                        onClick={handleSubmit}
                    >
                        {submitting ? <MiniSpinner /> : editingId === null ? "Signatur anlegen" : "Speichern"}
                    </button>
                    {editingId !== null && (
                        <button type="button" className="mset-btn" disabled={submitting} onClick={resetForm}>
                            Abbrechen
                        </button>
                    )}
                    <button type="button" className="mset-btn" onClick={refreshPreview}>
                        Vorschau aktualisieren
                    </button>
                </div>

                {preview.trim() && (
                    <div className="mset-block">
                        <label className="mset-label">Vorschau</label>
                        <div className="mset-preview">
                            <SandboxedMailBody html={preview} blockRemote={false} minHeight={60} />
                        </div>
                    </div>
                )}
            </div>

            <div className="mset-card">
                <h2 className="mset-title">Vorhandene Signaturen ({rows.length})</h2>
                {rows.length === 0 ? (
                    <div className="mset-empty">Noch keine Signaturen angelegt.</div>
                ) : (
                    rows.map((row) => {
                        const busy = savingId === row.id;
                        const badge = describeSignatureScope(row, sendable);
                        const orphan = isOrphanMailbox(row, sendable);
                        return (
                            <div key={row.id} className={`mset-row${editingId === row.id ? " active" : ""}`}>
                                <div className="mset-row-main">
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                        <span className="mset-row-name">{row.name}</span>
                                        {badge && <span className="mset-badge">{badge}</span>}
                                        {orphan && (
                                            <span className="mset-badge warn" title="Für dieses Postfach besteht kein Senderecht mehr.">
                                                Postfach nicht mehr zugewiesen
                                            </span>
                                        )}
                                    </div>
                                    <div className="mset-row-meta">
                                        {row.scope === "tenant" ? "Ganzes Büro" : "Nur ich"}
                                        {!row.isOwn && " · von einer Kollegin/einem Kollegen"}
                                    </div>
                                </div>
                                <div className="mset-row-actions">
                                    <button
                                        type="button"
                                        className="mset-btn icon"
                                        title="Bearbeiten"
                                        disabled={busy}
                                        onClick={() => startEdit(row)}
                                    >
                                        {I.edit}
                                    </button>
                                    <button
                                        type="button"
                                        className="mset-btn icon danger"
                                        title="Löschen"
                                        disabled={busy}
                                        onClick={() => setConfirmRow(row)}
                                    >
                                        {busy ? <MiniSpinner /> : I.trash}
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <ConfirmDialog
                open={confirmRow !== null}
                title="Signatur löschen"
                message={<>Soll die Signatur „{confirmRow?.name}" wirklich gelöscht werden?</>}
                confirmLabel="Löschen"
                tone="danger"
                busy={savingId !== null}
                onConfirm={() => confirmRow && handleRemove(confirmRow)}
                onCancel={() => setConfirmRow(null)}
            />

            <Toast
                open={toast !== null}
                message={toast?.msg ?? ""}
                tone={toast?.tone}
                onClose={() => setToast(null)}
            />
        </div>
    );
};

export default MailSignatureSection;
